import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { PLAN_LIMITS, type ExamTarget } from '@titrate/shared';
import { api, ApiRequestError } from '../lib/api';
import { keys, useEntitlements, useMe, useMeta } from '../lib/queries';
import { Callout, ErrorNotice, Spinner } from '../components/ui';
import { CountrySelect } from '../components/CountrySelect';

export function Account() {
  const { data: me, isLoading } = useMe();
  const { data: ent } = useEntitlements();
  const { data: meta } = useMeta();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (isLoading) return <Spinner />;
  const user = me?.user;
  if (!user) return null;

  const tier = ent?.entitlements.tier ?? 'free';
  const limits = PLAN_LIMITS[tier];

  const openPortal = async () => {
    setError(null);
    try {
      const { url } = await api.post<{ url: string }>('/api/billing/portal');
      window.location.href = url;
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not open the billing portal.');
    }
  };

  const resendVerification = async () => {
    setError(null);
    try {
      await api.post('/api/auth/resend-verification');
      setNotice('Confirmation email sent. Check your inbox.');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not resend the confirmation email.');
    }
  };

  const deleteAccount = async () => {
    if (!window.confirm('Permanently delete your account and all your results? This cannot be undone.')) return;
    try {
      await api.delete('/api/account', { confirm: 'DELETE' });
      qc.clear();
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not delete your account.');
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className="text-3xl font-bold tracking-tight text-ink">Account</h1>

      {notice ? <Callout>{notice}</Callout> : null}
      {error ? <ErrorNotice message={error} /> : null}

      {!user.emailVerified ? (
        <section className="card border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4 sm:p-6">
          <h2 className="text-base font-semibold text-amber-900 dark:text-amber-200">Confirm your email address</h2>
          <p className="mt-1.5 text-sm text-amber-800 dark:text-amber-200">
            AI feedback reports, revision timetables and the AI tutor are locked until you confirm{' '}
            <strong>{user.email}</strong>.
          </p>
          <button type="button" className="btn-primary mt-4" onClick={() => void resendVerification()}>
            Resend confirmation email
          </button>
          {meta && !meta.features.email ? (
            <p className="mt-3 text-xs text-amber-700 dark:text-amber-300">
              Email sending is not configured on this deployment, so the link is written to the server
              log instead of being delivered.
            </p>
          ) : null}
        </section>
      ) : null}

      <ProfileSection user={user} onSaved={() => void qc.invalidateQueries({ queryKey: keys.me })} />

      <section className="card p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-ink">Subscription</h2>
        <p className="mt-2 text-sm text-muted">
          You are on the <strong>{limits.label}</strong> plan
          {ent?.entitlements.currentPeriodEnd
            ? `, renewing ${new Date(ent.entitlements.currentPeriodEnd).toLocaleDateString('en-AU', { dateStyle: 'medium' })}`
            : ''}
          {ent?.entitlements.cancelAtPeriodEnd ? ' (cancels at the end of the period)' : ''}.
        </p>

        {ent ? (
          <dl className="mt-4 grid gap-3 sm:grid-cols-3">
            {(
              [
                ['Feedback reports', 'feedback_report'],
                ['Revision timetables', 'revision_plan'],
                ['Tutor messages', 'tutor_message'],
              ] as const
            ).map(([label, key]) => (
              <div key={key} className="rounded-lg border border-line p-3">
                <dt className="text-xs text-muted">{label}</dt>
                <dd className="mt-0.5 text-lg font-bold text-ink">
                  {ent.entitlements.remaining[key] === null ? 'Unlimited' : `${ent.entitlements.remaining[key]} left`}
                </dd>
                <p className="mt-0.5 text-xs text-subtle">{ent.entitlements.used[key]} used this period</p>
              </div>
            ))}
          </dl>
        ) : null}

        <div className="mt-5 flex flex-wrap gap-2">
          {meta?.features.billing ? (
            <button type="button" className="btn-secondary" onClick={() => void openPortal()}>
              Manage billing
            </button>
          ) : null}
          <Link to="/pricing" className="btn-primary">
            {tier === 'free' ? 'Upgrade' : 'Change plan'}
          </Link>
        </div>
      </section>

      <PasswordSection />

      <section className="card p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-ink">Your data</h2>
        <p className="mt-1.5 text-sm text-muted">Download everything we hold about you.</p>
        <div className="mt-4">
          <a href="/api/account/export" className="btn-secondary" download>
            Export my data (JSON)
          </a>
        </div>

        {/*
          Its own block with a rule above it. In the previous single flex row
          these two stacked into equal full-width buttons on a phone, putting
          an irreversible delete directly under a harmless download.
        */}
        <div className="mt-6 border-t border-line pt-5">
          <h3 className="text-sm font-semibold text-red-700 dark:text-red-300">Delete your account</h3>
          <p className="mt-1.5 text-sm text-muted">
            Removes your account and every result permanently. This cannot be undone.
          </p>
          <button
            type="button"
            className="btn mt-4 border border-red-300 dark:border-red-500/40 bg-surface text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/15"
            onClick={() => void deleteAccount()}
          >
            Delete my account
          </button>
        </div>
      </section>
    </div>
  );
}

function ProfileSection({
  user,
  onSaved,
}: {
  user: {
    firstName: string | null;
    lastName: string | null;
    country: string | null;
    marketingOptIn: boolean;
    examTarget: ExamTarget | null;
    examDate: string | null;
    weeklyStudyHours: number | null;
  };
  onSaved: () => void;
}) {
  const [firstName, setFirstName] = useState(user.firstName ?? '');
  const [lastName, setLastName] = useState(user.lastName ?? '');
  const [country, setCountry] = useState(user.country ?? '');
  const [marketingOptIn, setMarketingOptIn] = useState(user.marketingOptIn);
  const [examTarget, setExamTarget] = useState<ExamTarget>(user.examTarget ?? 'CAOP');
  const [examDate, setExamDate] = useState(user.examDate ?? '');
  const [hours, setHours] = useState(user.weeklyStudyHours?.toString() ?? '');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setSaved(false);
    try {
      await api.patch('/api/account/profile', {
        ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
        ...(country ? { country } : {}),
        marketingOptIn,
        examTarget,
        examDate: examDate || null,
        weeklyStudyHours: hours ? Number(hours) : null,
      });
      onSaved();
      setSaved(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card space-y-4 p-4 sm:p-6">
      <h2 className="text-lg font-semibold text-ink">Study profile</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="afirst">First name</label>
          <input id="afirst" className="input" maxLength={80} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="alast">Last name</label>
          <input id="alast" className="input" maxLength={80} value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
      </div>
      <div>
        <label className="label" htmlFor="acountry">Country</label>
        <CountrySelect id="acountry" value={country} onChange={setCountry} />
      </div>
      <div>
        <label className="label" htmlFor="atarget">Exam</label>
        <select id="atarget" className="input" value={examTarget} onChange={(e) => setExamTarget(e.target.value as ExamTarget)}>
          <option value="CAOP">CAOP</option>
          <option value="OPRA">OPRA</option>
          <option value="BOTH">Both</option>
        </select>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="adate">Exam date</label>
          <input id="adate" type="date" className="input" value={examDate} onChange={(e) => setExamDate(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="ahours">Study hours per week</label>
          <input id="ahours" type="number" min={1} max={80} className="input" value={hours} onChange={(e) => setHours(e.target.value)} />
        </div>
      </div>
      <label className="flex cursor-pointer gap-3 rounded-lg border border-line p-3">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-brand-700"
          checked={marketingOptIn}
          onChange={(e) => setMarketingOptIn(e.target.checked)}
        />
        <span className="text-sm text-muted">
          Email me study tips and product updates. Account and billing emails are sent regardless.
        </span>
      </label>
      <div className="flex items-center gap-3">
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
        {saved ? <span className="text-sm text-emerald-700 dark:text-emerald-300">Saved</span> : null}
      </div>
    </form>
  );
}

/**
 * Always rendered. A Google-only account has no password to change, and the API
 * says so clearly — which is better than hiding the section and leaving the user
 * wondering where it went.
 */
function PasswordSection() {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.post('/api/auth/change-password', { currentPassword, newPassword });
      setMessage('Password changed.');
      setCurrent('');
      setNew('');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not change your password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card space-y-4 p-4 sm:p-6">
      <h2 className="text-lg font-semibold text-ink">Change password</h2>
      {error ? <ErrorNotice message={error} /> : null}
      {message ? <p className="text-sm text-emerald-700 dark:text-emerald-300">{message}</p> : null}
      <div>
        <label className="label" htmlFor="cpw">Current password</label>
        <input id="cpw" type="password" className="input" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrent(e.target.value)} />
      </div>
      <div>
        <label className="label" htmlFor="npw">New password</label>
        <input id="npw" type="password" minLength={10} className="input" autoComplete="new-password" value={newPassword} onChange={(e) => setNew(e.target.value)} />
      </div>
      <button type="submit" className="btn-secondary" disabled={busy || !currentPassword || newPassword.length < 10}>
        {busy ? 'Saving…' : 'Change password'}
      </button>
    </form>
  );
}
